import type {
  PerencanaanGuruDetailData,
  PerencanaanItemReviewDiff,
} from 'app/KinerjaGuruPage/types/perencanaan'
import type { Kinerja, KinerjaUtamaDetail } from 'types/kinerja'

// We need to separate the kinerja utama into the mandatory tugas pokok
// and the selectable tugas pokok. The selectable tugas pokok will have
// `tag` property set to `option-ku-1`.
export function resolveTugasPokokContent(
  kinerjaUtamaItems: Array<KinerjaUtamaDetail>
): {
  selectable: Array<KinerjaUtamaDetail>
  mandatory: Array<KinerjaUtamaDetail>
} {
  const selectable: Array<KinerjaUtamaDetail> = []
  const mandatory: Array<KinerjaUtamaDetail> = []

  kinerjaUtamaItems.forEach((item) => {
    if (isSelectableTugasPokok(item.tag)) {
      selectable.push(item)
    } else {
      mandatory.push(item)
    }
  })

  return { selectable, mandatory }
}

export function isSelectableTugasPokok(tag: string): boolean {
  return tag === 'option-ku-1'
}

/** @deprecated use `resolvePerencanaanDetailData` from `app/KinerjaShared/utils/perencanaan.tsx` instead. */
export function resolvePerencanaanDetailData(
  data: Kinerja
): PerencanaanGuruDetailData {
  if (!data) {
    return {
      tugasTambahanIds: [],
      tugasPokokIds: [],
      praktikKinerjaId: null,
      pengembanganKompetensiId: null,
      perilakuKerjaItems: [],
      reviewTugasTambahanIds: [],
      reviewTugasPokokIds: [],
      reviewPraktikKinerjaId: null,
      reviewPengembanganKompetensiId: null,
      reviewPerilakuKerjaItems: [],
    }
  }

  return {
    tugasTambahanIds: data.tugasTambahan
      // Filter out placeholder item with id=0
      ?.filter((item) => item.id !== 0)
      .map((item) => item.id),
    tugasPokokIds: data.kinerjaUtama,
    // Use praktik pembelajaran id as praktik kinerja id
    praktikKinerjaId: data.praktikPembelajaran,
    // Safely get the first item of the `pengembanganKompetensi` array
    // since it always have only 1 item
    pengembanganKompetensiId: data.pengembanganKompetensi?.[0]?.id,
    perilakuKerjaItems: data.prilakuKerja,
    reviewTugasTambahanIds: data.reviewTugasTambahan
      // Filter out placeholder item with id=0
      ?.filter((item) => item.id !== 0)
      .map((item) => item.id),
    reviewTugasPokokIds: data.reviewKinerjaUtama,
    reviewPraktikKinerjaId: data.reviewPraktikPembelajaran,
    reviewPengembanganKompetensiId: data.reviewPengembanganKompetensi?.[0]?.id,
    reviewPerilakuKerjaItems: data.reviewPrilakuKerja,
  }
}

/**
 * @deprecated use `resolvePerencanaanReviewDiff` from `app/KinerjaShared/utils/perencanaan.tsx` instead.
 * @see {@link https://github.com/wartek-id/kinerja-web/blob/main/app/KinerjaShared/utils/perencanaan.tsx}
 * Compares two arrays of item IDs: the original set and the reviewed (modified) set,
 * and returns an array describing the changes for each item. Each result entry
 * contains the original and reviewed value at the same index, and a flag indicating
 * whether the review changed the item, added a new item, or left it unchanged.
 *
 * @param originalIds - Array of original item IDs before review.
 * @param reviewIds - Array of item IDs after review (may be modified).
 * @returns Array of objects for each index, showing the original, the review, and whether it was changed.
 */
export function resolvePerencanaanReviewDiff(
  originalIds: Array<number>,
  reviewIds: Array<number>
): Array<PerencanaanItemReviewDiff<number>> {
  const items: Array<PerencanaanItemReviewDiff<number>> = []

  for (let i = 0; i < reviewIds.length; i++) {
    const hasOriginal = !!originalIds[i]
    const hasReview = !!reviewIds[i]
    const hasDiff = hasReview && originalIds[i] !== reviewIds[i]
    const isAddedUponReview = hasReview && !hasOriginal
    const isChanged = hasDiff || isAddedUponReview

    items.push({
      isChanged,
      original: originalIds[i] ?? null,
      review: reviewIds[i] ?? null,
    })
  }

  return items
}
